import { GridApi, RowNode } from '@ag-grid-enterprise/all-modules';
import { Box, Button, ButtonGroup } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { KeyboardArrowLeft, KeyboardArrowRight, KeyboardBackspace } from '@material-ui/icons';
import { Link, navigate, useParams } from '@reach/router';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

import { BenchmarkLinkState } from '../../../../../components/TableGrid/Renderers/LinkCellRenderer/BenchmarkLinkCellRenderer';
import { useTableGridApiState } from '../../../../../components/TableGrid/TableGridApiContext/TableGridApiContext';
import { CONSTANTS } from '../../../../../core/config/constants';
import { BorderWidthWithColor, FontSizes } from '../../../../../core/config/mainTheme';
import { getPath } from '../../../../../core/utils/routes.utils';
import { ROUTES } from '../../../../../routes';
import { useCompaniesState } from '../../../shared/CompanyContext/CompanyContext';
import { useFilterState } from '../../../shared/FinancialDataContext/FinancialDataContext';

interface NavigationHeaderProps {
  gridApi?: GridApi;
}

const NavigationHeader: React.FC<NavigationHeaderProps> = () => {
  const classes = useStyles();
  const { companyUid, benchmarkId } = useParams();
  const { companies, pagination, dataset, dbVersion } = useCompaniesState();
  const { tableGridApi } = useTableGridApiState();
  const { isRefinement } = useFilterState();
  const [visibleRows, setVisibleRows] = useState<number>(0);
  const [prevDisable, setPrevDisable] = useState<boolean>(true);
  const [nextDisable, setNexDisable] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<any>(null);
  const { t } = useTranslation();

  const linkState: BenchmarkLinkState = {
    switchToRefinementTab: isRefinement,
  };

  const backToSearchUrl = isRefinement
    ? generatePath(ROUTES.benchmarking.benchmark, {
        benchmarkId: benchmarkId,
      })
    : generatePath(ROUTES.financialData.page);

  const getFirstIndex = () => {
    return (pagination.page > 0 ? pagination.page - 1 : 0) * pagination.size;
  };

  const getLastIndex = () => {
    return getFirstIndex() + visibleRows;
  };

  useEffect(() => {
    if (companies) {
      setVisibleRows(companies.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, pagination.page]);

  useEffect(() => {
    setPrevDisable(
      selectedIndex === getFirstIndex() ||
        selectedIndex < getFirstIndex() ||
        selectedIndex > getLastIndex() ||
        selectedIndex === null,
    );
    setNexDisable(selectedIndex === visibleRows - 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, visibleRows, pagination.page]);

  useEffect(() => {
    if (tableGridApi) {
      tableGridApi.forEachNode((rowNode: RowNode) => {
        if (companyUid === rowNode?.data?.companyInfo?.id) {
          setSelectedIndex(rowNode.rowIndex);
        }
      });
    }
  }, [tableGridApi, companyUid, companies]);

  useEffect(() => {
    if (tableGridApi && selectedIndex !== null) {
      tableGridApi.forEachNode((rowNode: RowNode) => {
        if (selectedIndex === rowNode.rowIndex) {
          rowNode.setSelected(true);
        }
      });
    }
  }, [tableGridApi, selectedIndex]);

  const handleClick = (direction: string) => {
    if (tableGridApi) {
      let rowNode = tableGridApi.getSelectedNodes()[0]
        ? tableGridApi.getSelectedNodes()[0]
        : tableGridApi.getRowNode(getFirstIndex().toString());

      let rowIndex = rowNode.rowIndex;

      if (rowIndex < getFirstIndex() || rowIndex > getLastIndex() || selectedIndex === null) {
        rowIndex = getFirstIndex() - 1;
      }

      switch (direction) {
        case CONSTANTS.NAVIGATE_NEXT:
          rowNode = tableGridApi.getRowNode(`${rowIndex + 1}`);
          break;
        case CONSTANTS.NAVIGATE_PREV:
          rowNode = tableGridApi.getRowNode(`${rowIndex - 1}`);
          break;
      }

      setSelectedIndex(rowNode.rowIndex);

      const path = getPath(ROUTES.benchmarking.refinementCompany, ROUTES.financialData.company, {
        benchmarkId,
        companyUid: rowNode.data.companyInfo.id,
        dataset,
        dbVersion,
      });
      navigate(path);
    }
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.backlinkBox}>
        <Link to={backToSearchUrl} className={classes.backlink} state={linkState}>
          <KeyboardBackspace /> {t('companies:backToSearch')}
        </Link>
      </Box>
      <ButtonGroup size="small" className={classes.buttonGroup}>
        <Button
          onClick={() => handleClick(CONSTANTS.NAVIGATE_PREV)}
          className={prevDisable ? 'disabled' : ''}
        >
          <KeyboardArrowLeft />
        </Button>
        <Button
          onClick={() => handleClick(CONSTANTS.NAVIGATE_NEXT)}
          className={nextDisable ? 'disabled' : ''}
        >
          <KeyboardArrowRight />
        </Button>
      </ButtonGroup>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexWrap: 'nowrap',
    backgroundColor: theme.palette.secondary.main,
    height: theme.spacing(11),
    border: BorderWidthWithColor(1, theme.palette.divider),
  },
  backlink: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    textDecoration: 'none',
    textTransform: 'uppercase',
    color: theme.palette.secondary.dark,
    fontSize: FontSizes.size.smallest,
    '& .MuiSvgIcon-root': {
      marginRight: theme.spacing(2),
      width: theme.spacing(8),
    },
  },
  backlinkBox: {
    display: 'flex',
    flexWrap: 'nowrap',
  },
  buttonGroup: {
    borderRadius: 0,
    marginLeft: 'auto',
    '& .MuiButton-root': {
      borderRadius: 0,
      borderTop: 0,
      borderBottom: 0,
      color: theme.palette.secondary.dark,
      borderRight: 'none',
      borderLeft: BorderWidthWithColor(1, theme.palette.divider),
      '&.disabled': {
        pointerEvents: 'none',
      },
    },
  },
}));

export default NavigationHeader;
